import React, {forwardRef, useId} from 'react';
import clsx from 'clsx';

export type IconSizeToken = '2xs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl';
export type IconSize =
  | IconSizeToken
  | number
  | `${number}px`
  | `${number}rem`
  | `${number}em`
  | string;

export interface SvgIconProps extends React.SVGAttributes<SVGElement> {
  children?: React.ReactNode;
  size?: IconSize;
  color?: string;
  /** Defaults to 'filled' for modern sets. Use 'outline' for legacy line icons. */
  variant?: 'filled' | 'outline';
  'aria-label'?: string;
  title?: string;
}

export const SvgIcon = forwardRef<SVGSVGElement, SvgIconProps & {attr?: {}}>(
  (props, ref) => {
    const {
      attr,
      size,
      title,
      className,
      color,
      style,
      children,
      viewBox,
      width,
      height,
      variant = 'filled',
      // Allow explicit overrides from caller
      stroke,
      fill,
      strokeWidth,
      'aria-label': ariaLabel,
      role,
      ...svgProps
    } = props;

    const titleId = useId();
    const labelled = Boolean(ariaLabel || title);
    const resolvedRole = role ?? (labelled ? 'img' : undefined);
    const ariaHidden = labelled ? undefined : true;

    const {sizeClass, explicitSize} = resolveSize(size);

    // Modern defaults (match MUI): filled by default, no stroke.
    const isOutline = variant === 'outline';
    const defaultFill = isOutline ? 'none' : 'currentColor';
    const defaultStroke = isOutline ? 'currentColor' : 'none';

    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        focusable={false}
        aria-hidden={ariaHidden}
        aria-label={ariaLabel}
        aria-labelledby={title ? titleId : undefined}
        role={resolvedRole}
        viewBox={viewBox || '0 0 24 24'}
        {...attr}
        {...svgProps}
        ref={ref}
        className={clsx('svg-icon', className, sizeClass)}
        style={{color, ...style}}
        width={width ?? explicitSize}
        height={height ?? explicitSize}
        fill={fill ?? defaultFill}
        stroke={stroke ?? defaultStroke}
        strokeWidth={strokeWidth}
        // Keep non-scaling only for outline variant
        vectorEffect={isOutline ? 'non-scaling-stroke' : undefined}
      >
        {title ? <title id={titleId}>{title}</title> : null}
        {children}
      </svg>
    );
  }
);

SvgIcon.displayName = 'SvgIcon';

function resolveSize(size?: IconSize): {sizeClass?: string; explicitSize?: string | number} {
  switch (size) {
    case '2xs': return {sizeClass: 'icon-2xs'};
    case 'xs':  return {sizeClass: 'icon-xs'};
    case 'sm':  return {sizeClass: 'icon-sm'};
    case 'md':  return {sizeClass: 'icon-md'};
    case 'lg':  return {sizeClass: 'icon-lg'};
    case 'xl':  return {sizeClass: 'icon-xl'};
    default: {
      if (size == null) return {explicitSize: '1em'};
      if (typeof size === 'number') return {explicitSize: size};
      return {explicitSize: size};
    }
  }
}
